import { UserProgress } from '../services/storage';
import { vocabularyCategories, vocabularyData } from '../data/vocabulary';
import { ttsService } from '../services/tts';
import StatCard from '../components/common/StatCard';

interface HomePageProps {
  user: UserProgress;
  onNavigate?: (page: string) => void;
}

export default function HomePage({ user, onNavigate }: HomePageProps) {
  const recentWords = vocabularyData.slice(0, 5);
  const progressPercentage = Math.min(100, (user.points / (user.level * 100)) * 100);

  const handlePlayWord = async (word: typeof vocabularyData[0]) => {
    try {
      await ttsService.speakSlowly(word.word);
    } catch (error) {
      console.error('TTS error:', error);
    }
  };

  return (
    <div className="space-y-8 animate-slide-up">
      {/* Welcome banner */}
      <div className="bg-gradient-to-r from-indigo-500 to-purple-500 rounded-3xl p-8 text-white shadow-xl">
        <div className="flex items-center justify-between">
          <div>
            <h1 className="text-3xl font-bold mb-2">
              你好，{user.name}！👋
            </h1>
            <p className="text-white/80">
              今天想学什么呢？
            </p>
          </div>
          <div className="text-6xl animate-float">{user.avatar}</div>
        </div>

        {/* Phonetic info badge */}
        <div className="mt-4 bg-white/20 rounded-2xl p-3 flex items-center justify-between">
          <div className="flex items-center gap-2">
            <span className="text-xl">📖</span>
            <span className="text-sm">所有单词都标注了音标，点击音标可以查看详细说明</span>
          </div>
        </div>
      </div>

      {/* Quick stats */}
      <div className="grid grid-cols-2 md:grid-cols-4 gap-4">
        <StatCard
          icon="⭐"
          label="积分"
          value={user.points}
          color="yellow"
        />
        <StatCard
          icon="📊"
          label="等级"
          value={`Lv.${user.level}`}
          color="purple"
        />
        <StatCard
          icon="📚"
          label="单词"
          value={user.wordsLearned.length}
          color="blue"
        />
        <StatCard
          icon="🔥"
          label="连续"
          value={`${user.streakDays}天`}
          color="orange"
        />
      </div>

      {/* Level progress */}
      <div className="bg-white rounded-3xl p-6 shadow-soft">
        <div className="flex items-center justify-between mb-3">
          <h3 className="font-bold text-gray-700">等级进度</h3>
          <span className="text-sm text-gray-500">
            {user.points} / {user.level * 100} 分
          </span>
        </div>
        <div className="progress-bar">
          <div
            className="progress-bar-fill"
            style={{ width: `${progressPercentage}%` }}
          />
        </div>
        <p className="text-sm text-gray-500 mt-2">
          再获得 {user.level * 100 - user.points} 分即可升级！
        </p>
      </div>

      {/* Quick start */}
      <div>
        <h2 className="text-2xl font-bold text-gray-800 mb-4">快速开始</h2>
        <div className="grid grid-cols-2 md:grid-cols-4 gap-4">
          <QuickStartCard
            icon="📚"
            title="学单词"
            description="学习新单词"
            color="from-blue-400 to-blue-600"
            onClick={() => onNavigate?.('vocabulary')}
          />
          <QuickStartCard
            icon="🎧"
            title="练听力"
            description="听音频练耳朵"
            color="from-green-400 to-green-600"
            onClick={() => onNavigate?.('listening')}
          />
          <QuickStartCard
            icon="🎤"
            title="练口语"
            description="跟读练习"
            color="from-pink-400 to-pink-600"
            onClick={() => onNavigate?.('speaking')}
          />
          <QuickStartCard
            icon="📖"
            title="自然拼读"
            description="Phonics 规则"
            color="from-teal-400 to-green-600"
            onClick={() => onNavigate?.('natural-phonics')}
          />
        </div>
      </div>

      {/* Phonics section */}
      <div>
        <h2 className="text-2xl font-bold text-gray-800 mb-4">发音学习</h2>
        <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
          <div className="bg-gradient-to-r from-teal-100 to-green-100 rounded-3xl p-6 shadow-soft">
            <div className="flex items-center justify-between">
              <div>
                <p className="text-lg font-bold text-gray-700">📖 自然拼读 Phonics</p>
                <p className="text-sm text-gray-500 mb-3">学习发音规则，掌握拼读技巧</p>
                <p className="text-xs text-gray-600">✅ 美式/英式发音对比</p>
                <p className="text-xs text-gray-600">✅ 系统化规则学习</p>
              </div>
              <button
                onClick={() => onNavigate?.('natural-phonics')}
                className="px-6 py-3 bg-teal-500 text-white rounded-xl font-bold hover:bg-teal-600"
              >
                开始学习 →
              </button>
            </div>
          </div>

          <div className="bg-gradient-to-r from-purple-100 to-pink-100 rounded-3xl p-6 shadow-soft">
            <div className="flex items-center justify-between">
              <div>
                <p className="text-lg font-bold text-gray-700">🎵 音标表</p>
                <p className="text-sm text-gray-500 mb-3">IPA 国际音标查询</p>
                <p className="text-xs text-gray-600">✅ 元音/辅音分类</p>
                <p className="text-xs text-gray-600">✅ 音标详细说明</p>
              </div>
              <button
                onClick={() => onNavigate?.('phonetics')}
                className="px-6 py-3 bg-purple-500 text-white rounded-xl font-bold hover:bg-purple-600"
              >
                查看音标 →
              </button>
            </div>
          </div>
        </div>
      </div>

      {/* Daily goals & motivation */}
      <div>
        <h2 className="text-2xl font-bold text-gray-800 mb-4">每日任务</h2>
        <div className="grid grid-cols-2 md:grid-cols-3 gap-4">
          <QuickStartCard
            icon="📅"
            title="每日打卡"
            description="完成任务获奖励"
            color="from-yellow-400 to-orange-500"
            onClick={() => onNavigate?.('daily-task')}
          />
          <QuickStartCard
            icon="📝"
            title="错题本"
            description="复习错题巩固"
            color="from-red-400 to-pink-500"
            onClick={() => onNavigate?.('mistake-review')}
          />
          <QuickStartCard
            icon="🐾"
            title="我的宠物"
            description="喂养宠物成长"
            color="from-pink-400 to-purple-500"
            onClick={() => onNavigate?.('virtual-pet')}
          />
        </div>
      </div>

      {/* KET Training */}
      <div>
        <h2 className="text-2xl font-bold text-gray-800 mb-4">🎯 KET 考试备考</h2>
        <div className="bg-gradient-to-r from-blue-100 to-indigo-100 rounded-3xl p-6 shadow-soft">
          <div className="flex flex-col sm:flex-row sm:items-start sm:justify-between gap-4">
            {/* Left: Info */}
            <div className="flex-1">
              <div className="flex items-center gap-2 mb-3">
                <span className="text-3xl">🎓</span>
                <p className="text-xl font-bold text-gray-700">KET 剑桥英语考试训练</p>
              </div>
              <p className="text-sm text-gray-500 mb-4">1000+词 · 8周学习计划 · 每日挑战 · 词汇测验</p>

              {/* Feature cards - 2 columns on mobile, 4 on desktop */}
              <div className="grid grid-cols-2 sm:grid-cols-4 gap-3">
                <div className="bg-white/70 rounded-xl p-3 text-center shadow-sm">
                  <p className="text-2xl mb-1">📚</p>
                  <p className="font-bold text-indigo-600 text-sm">12 主题</p>
                </div>
                <div className="bg-white/70 rounded-xl p-3 text-center shadow-sm">
                  <p className="text-2xl mb-1">📖</p>
                  <p className="font-bold text-indigo-600 text-sm">1741 词</p>
                </div>
                <div className="bg-white/70 rounded-xl p-3 text-center shadow-sm">
                  <p className="text-2xl mb-1">📅</p>
                  <p className="font-bold text-indigo-600 text-sm">8 周计划</p>
                </div>
                <div className="bg-white/70 rounded-xl p-3 text-center shadow-sm">
                  <p className="text-2xl mb-1">✍️</p>
                  <p className="font-bold text-indigo-600 text-sm">测验功能</p>
                </div>
              </div>
            </div>

            {/* Right: Button - moved up */}
            <button
              onClick={() => onNavigate?.('ket-training')}
              className="w-full sm:w-auto px-8 py-4 bg-indigo-500 text-white rounded-xl font-bold hover:bg-indigo-600 transition-all shadow-lg hover:shadow-xl hover:scale-105 flex items-center justify-center gap-2 whitespace-nowrap"
            >
              <span className="text-lg">🎯</span>
              <span>开始备考</span>
            </button>
          </div>

          {/* Bottom info */}
          <div className="mt-4 bg-white/40 rounded-xl p-3">
            <p className="text-xs text-gray-600">
              ✅ 适合小学四年级备考 Cambridge English: Key (KET/A2 Key) 考试 · 离线学习 · 进度保存
            </p>
          </div>
        </div>
      </div>

      {/* Games */}
      <div>
        <h2 className="text-2xl font-bold text-gray-800 mb-4">趣味游戏</h2>
        <div className="bg-gradient-to-r from-purple-100 to-pink-100 rounded-3xl p-6 shadow-soft">
          <div className="flex items-center justify-between">
            <div>
              <p className="text-lg font-bold text-gray-700">🎮 游戏乐园</p>
              <p className="text-sm text-gray-500">通过游戏快乐学英语</p>
            </div>
            <button
              onClick={() => onNavigate?.('games')}
              className="px-6 py-3 bg-purple-500 text-white rounded-xl font-bold hover:bg-purple-600"
            >
              开始玩 →
            </button>
          </div>
        </div>
      </div>

      {/* Vocabulary categories */}
      <div>
        <h2 className="text-2xl font-bold text-gray-800 mb-4">单词分类</h2>
        <div className="grid grid-cols-2 md:grid-cols-5 gap-4">
          {vocabularyCategories.map(category => (
            <button
              key={category.id}
              className="bg-white rounded-2xl p-4 shadow-soft card-hover text-center"
            >
              <span className="text-4xl mb-2 block">{category.icon}</span>
              <span className="text-sm font-medium text-gray-700">
                {category.name}
              </span>
            </button>
          ))}
        </div>
      </div>

      {/* Recent words */}
      <div>
        <h2 className="text-2xl font-bold text-gray-800 mb-4">最近学习的单词</h2>
        <div className="bg-white rounded-3xl shadow-soft overflow-hidden">
          <div className="divide-y divide-gray-100">
            {recentWords.map(word => (
              <div
                key={word.id}
                className="p-4 flex items-center gap-4 hover:bg-gray-50 transition-colors"
              >
                <span className="text-3xl">{word.image}</span>
                <div className="flex-1">
                  <p className="font-bold text-gray-800">{word.word}</p>
                  <p className="text-sm text-gray-500">{word.meaning}</p>
                </div>
                <button
                  onClick={(e) => {
                    e.stopPropagation();
                    handlePlayWord(word);
                  }}
                  className="text-2xl hover:scale-110 transition-transform cursor-pointer"
                  title="播放发音"
                >
                  ▶️
                </button>
              </div>
            ))}
          </div>
        </div>
      </div>

      {/* Motivational message */}
      <div className="bg-gradient-to-r from-yellow-100 to-orange-100 rounded-3xl p-6 text-center">
        <p className="text-2xl mb-2">🌟</p>
        <p className="text-gray-700 font-medium">
          {getMotivationMessage(user.points, user.streakDays)}
        </p>
      </div>
    </div>
  );
}

// Quick start card component
function QuickStartCard({ icon, title, description, color, onClick }: {
  icon: string;
  title: string;
  description: string;
  color: string;
  onClick?: () => void;
}) {
  return (
    <button
      onClick={onClick}
      className={`bg-gradient-to-r ${color} rounded-2xl p-6 text-white text-left hover:shadow-lg transition-all card-hover`}
    >
      <span className="text-4xl mb-2 block">{icon}</span>
      <h3 className="font-bold text-lg mb-1">{title}</h3>
      <p className="text-sm text-white/80">{description}</p>
    </button>
  );
}

// Get motivational message based on progress
function getMotivationMessage(points: number, streakDays: number): string {
  if (streakDays >= 7) return '太棒了！你坚持了一周，继续加油！🎉';
  if (streakDays >= 3) return '不错哦！已经坚持3天了，你很棒！💪';
  if (points >= 100) return '你学习很努力！继续加油！🌟';
  if (points >= 50) return '很好的开始！继续学习吧！😊';
  return '开始你的英语学习之旅吧！每一步都很重要！🌈';
}